// PrivacyPage.js — Privacy Policy for premierleaguescorigami.com. Written
// in plain language with the specifics AdSense requires (what we collect,
// third-party ads/cookies, rights, contact).

import React from "react";
import { Box, Container, Typography, Link } from "@mui/material";
import SEO from "./components/SEO";
import { BRAND } from "./theme";

const H2 = (props) => (
  <Typography
    component="h2"
    sx={{
      mt: 4,
      mb: 1.5,
      fontSize: { xs: "1.2rem", sm: "1.4rem" },
      fontWeight: 700,
      color: "#fff",
    }}
    {...props}
  />
);

const P = (props) => (
  <Typography
    sx={{
      color: BRAND.textSecondary,
      lineHeight: 1.8,
      fontSize: { xs: "0.95rem", sm: "1rem" },
      mb: 1.5,
    }}
    {...props}
  />
);

export default function PrivacyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy policy for Premier League Scorigami, covering what data we collect, how cookies and third-party ads are used, and how to contact us."
        path="/privacy"
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
        <Typography
          component="h1"
          sx={{
            background: BRAND.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "2rem", sm: "2.75rem" },
            fontWeight: 800,
            mb: 1,
          }}
        >
          Privacy Policy
        </Typography>
        <Typography sx={{ color: BRAND.textSecondary, mb: 3 }}>
          Last updated: September 23, 2026
        </Typography>

        <P>
          Premier League Scorigami ("the site", "we", "us") is an independent
          personal project. This page explains what information is collected
          when you visit the site, how it is used, and the choices available
          to you. By using the site you agree to the practices described
          here.
        </P>

        <H2>Information We Collect</H2>
        <P>
          We do not ask you to create an account and we do not collect
          personally identifying information directly. The site does use a
          small number of third-party services that may collect anonymized or
          pseudonymized information about your visit:
        </P>
        <Box component="ul" sx={{ color: BRAND.textSecondary, pl: 3, mb: 2 }}>
          <li>
            <strong>Hosting and delivery</strong> — pages and assets are
            served through Amazon CloudFront. CloudFront and its origin (an
            S3 bucket) automatically record standard HTTP request metadata
            such as your IP address, request timestamp, and user agent. This
            information is used to operate and secure the site.
          </li>
          <li>
            <strong>Google AdSense</strong> — if advertising is enabled on
            this site, Google and its partners may use cookies, web beacons
            or similar technologies to serve ads based on prior visits to
            this or other websites. See the section on advertising below.
          </li>
          <li>
            <strong>Google Analytics</strong> — measures how the site is used,
            such as which pages are viewed. See the section on analytics below.
          </li>
        </Box>
        <P>
          We do not sell personal information for money. The advertising and
          analytics services above do receive information about your visit,
          which some US state laws treat as a "sale" or "sharing" of personal
          information; see US state privacy rights below.
        </P>

        <H2>Cookies and Local Storage</H2>
        <P>
          The site itself uses only minimal browser storage, primarily to
          remember non-identifying UI preferences such as the currently
          selected season. Google Analytics sets first-party cookies to
          count visits; see the section on analytics below.
        </P>
        <P>
          Third-party services, including Google AdSense, may set their own
          cookies. You can review or delete cookies at any time through your
          browser settings.
        </P>

        <H2>Advertising</H2>
        <P>
          This site may display advertising served by Google AdSense. Google
          is a third-party vendor that uses cookies (including the DoubleClick
          cookie) to serve ads to our users based on their visit to this and
          other sites on the Internet.
        </P>
        <P>
          You can opt out of personalized advertising by visiting{" "}
          <Link
            href="https://www.google.com/settings/ads"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: BRAND.cyan }}
          >
            Google Ads Settings
          </Link>{" "}
          or by visiting{" "}
          <Link
            href="https://www.aboutads.info/choices/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: BRAND.cyan }}
          >
            aboutads.info
          </Link>{" "}
          (US) or{" "}
          <Link
            href="https://www.youronlinechoices.com/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: BRAND.cyan }}
          >
            youronlinechoices.com
          </Link>{" "}
          (EU/UK) to opt out of personalized advertising from participating
          vendors.
        </P>

        <H2>Analytics</H2>
        <P>
          We use Google Analytics 4 to understand how the site is used: which
          pages are viewed, which scorelines are opened or shared, the type of
          device and browser, approximate location (country or city), and how
          visitors arrived. Google Analytics sets first-party cookies (named
          _ga and _ga_ followed by an identifier) and does not store IP
          addresses. Visitors in the European Economic Area, the United Kingdom
          and Switzerland are asked for consent first; without it, Google
          Analytics sends only anonymous, cookieless signals. Analytics data is
          kept for 14 months. You can opt out on any site with Google's{" "}
          <Link
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: BRAND.cyan }}
          >
            opt-out browser add-on
          </Link>
          .
        </P>
        <P>
          Aggregate request metadata is also retained by our hosting provider
          for operational and security purposes.
        </P>

        <H2>Data Sources</H2>
        <P>
          Match data displayed on the site is sourced from publicly available
          Premier League fixtures and results. We do not publish any private
          or personal data about the site's visitors.
        </P>

        <H2>Children's Privacy</H2>
        <P>
          The site is not directed to children under 13 and we do not
          knowingly collect information from them.
        </P>

        <H2>Your Rights</H2>
        <P>
          Depending on where you live, you may have rights to access, correct,
          or delete information held about you. Because the site does not
          store personal information directly, most such requests should be
          directed to the third-party services described above. For questions
          about this site specifically, contact us using the email address on
          the <Link href="/contact" sx={{ color: BRAND.cyan }}>Contact</Link>{" "}
          page.
        </P>

        <H2>US State Privacy Rights</H2>
        <P>
          If you live in a US state with a consumer privacy law, such as
          California, Colorado, Connecticut, Virginia or Utah, you have the
          right to opt out of the "sale" or "sharing" of your personal
          information, including its use for targeted advertising. Visitors
          in those states see a "Do Not Sell or Share My Personal Information"
          link on this site. Opting out through it tells Google not to use
          information about your visit to personalize ads; you will still see
          ads, but they may be less relevant to you. You may also have the
          right to know about and delete information held about you, as
          described under Your Rights above.
        </P>

        <H2>Changes</H2>
        <P>
          We may update this policy from time to time. When we do, the
          "Last updated" date at the top of this page will change. Material
          changes will be highlighted on the site.
        </P>

        <H2>Contact</H2>
        <P>
          For any privacy-related questions, you can reach the site operator
          directly at{" "}
          <Link
            href="mailto:mason23dev@gmail.com"
            sx={{ color: BRAND.cyan }}
          >
            mason23dev@gmail.com
          </Link>
          , or via the{" "}
          <Link href="/contact" sx={{ color: BRAND.cyan }}>Contact</Link> page.
        </P>
      </Container>
    </>
  );
}
